import { useEffect, useRef, useState } from "react";
import { useOptionalAuth } from "../auth.js";
import { ActionConfirmation } from "../ui/ActionConfirmation.js";
import { idempotencyKey, type LessonCreditSummary } from "./api.js";
import { bookingFailureKind } from "./attempt.js";
import { diaryManualCreditUseAllowed, diaryManualCreditUseMessages, useExistingLessonCredit } from "./diaryPayments.js";
import { staffBooking } from "./staff.js";
import { notifyStudentCreditStateChanged } from "./studentCreditEvents.js";
import { checkSelectedLessons, selectedCreditProblem, selectedLessonLabel, type CheckedLesson, type SelectedLesson } from "./selectedLessons.js";

type CreditItem = Readonly<{ lesson: SelectedLesson; key: string; state: "pending" | "sending" | "applied" | "failed"; message?: string }>;
interface CreditOperation { readonly blockId: string; readonly items: readonly CreditItem[] }

function replaceCreditItem(operation: CreditOperation, item: CreditItem): CreditOperation {
  return { ...operation, items: operation.items.map(previous => previous.key === item.key ? item : previous) };
}

export function SelectedLessonCredit({ studentId, lessons, onBusyChange, onChanged, onDiscard }: {
  studentId: string;
  lessons: SelectedLesson[];
  onBusyChange: (busy: boolean) => void;
  onChanged: () => void;
  onDiscard?: () => void;
}) {
  const auth = useOptionalAuth();
  const storageKey = `wmf:selected-lesson-credit:${auth?.me?.user.id ?? "staff"}:${studentId}`;
  const [recovery] = useState(() => {
    try {
      const saved = JSON.parse(sessionStorage.getItem(storageKey) ?? "null") as CreditOperation | null;
      if (saved && (typeof saved.blockId !== "string" || !Array.isArray(saved.items) || !saved.items.length || saved.items.length > 50 || saved.items.some(i => !i.lesson?.bookingId || !Number.isSafeInteger(i.lesson.appointmentId) || !i.key || !["pending", "sending", "applied", "failed"].includes(i.state)))) throw new Error();
      return { operation: saved, error: null };
    } catch { return { operation: null, error: "The saved credit attempt could not be read. Ask the office to check credits before applying another." }; }
  });
  const [operation, setOperation] = useState<CreditOperation | null>(recovery.operation);
  const [summary, setSummary] = useState<LessonCreditSummary | null>(null);
  const [checked, setChecked] = useState<CheckedLesson[] | null>(null);
  const [blockId, setBlockId] = useState("");
  const [problem, setProblem] = useState<string | null>(recovery.error);
  const [busy, setBusy] = useState(false);
  const [balance, setBalance] = useState<number | null>(null);
  const locked = useRef(false);
  const [revision, setRevision] = useState(0);
  const changed = useRef(onChanged);
  changed.current = onChanged;
  const hasRecovery = recovery.operation !== null || recovery.error !== null;

  useEffect(() => {
    if (hasRecovery) return;
    let current = true;
    setSummary(null);
    setChecked(null);
    void Promise.all([staffBooking.lessonCredits(studentId), checkSelectedLessons(studentId, lessons, true)])
      .then(([credits, rows]) => {
        if (!current) return;
        if (credits.portal_student_id !== studentId) throw new Error("The credit balance did not match this student.");
        setSummary(credits);
        setChecked(rows);
        setBlockId(credits.blocks.find(b => b.available_credits > 0 && diaryManualCreditUseAllowed(b) && rows.some(r => !selectedCreditProblem(r, b)))?.lesson_block_id ?? "");
      })
      .catch(cause => { if (current) setProblem(cause instanceof Error ? cause.message : "Credits could not be checked."); });
    return () => { current = false; };
  }, [studentId, lessons, revision, hasRecovery]);

  const blocks = summary?.blocks.filter(b => b.available_credits > 0 && diaryManualCreditUseAllowed(b)) ?? [];
  const block = blocks.find(b => b.lesson_block_id === blockId);
  const eligible = block ? (checked ?? []).filter(r => !selectedCreditProblem(r, block)) : [];

  function saveOperation(next: CreditOperation) {
    // Persist before every write: a reload or closed drawer keeps the same debit key.
    sessionStorage.setItem(storageKey, JSON.stringify(next));
    setOperation(next);
  }

  function discardUnstarted() {
    if (locked.current || recovery.error || !operation || operation.items.some(i => i.state === "sending") || !operation.items.some(i => i.state === "pending")) return;
    try {
      // Only discard the unchanged journal when no debit needs checking.
      // A changed or unreadable journal may still contain an uncertain debit.
      if (sessionStorage.getItem(storageKey) !== JSON.stringify(operation)) throw new Error("The saved credit attempt changed. Close and reopen this drawer to check it before discarding credits.");
      sessionStorage.removeItem(storageKey);
      setOperation({ ...operation, items: operation.items.map(item => item.state === "pending" ? { ...item, state: "failed", message: "Not attempted · Discarded" } : item) });
      setProblem(null);
      onDiscard?.();
    } catch (cause) {
      setProblem(cause instanceof Error ? cause.message : "The unstarted credits could not be discarded. Keep this attempt open and try again.");
    }
  }

  async function run(saved?: CreditOperation) {
    if (locked.current || recovery.error) return;
    if (!saved && (!block || !eligible.length || eligible.length > block.available_credits)) return;
    locked.current = true;
    setBusy(true);
    onBusyChange(true);
    setProblem(null);
    let next = saved;
    try {
      if (!next) {
        const fresh = await staffBooking.lessonCredits(studentId);
        const freshBlock = fresh.blocks.find(b => b.lesson_block_id === blockId);
        const rows = await checkSelectedLessons(studentId, eligible.map(r => r.lesson), true);
        if (fresh.portal_student_id !== studentId || !freshBlock || freshBlock.available_credits < eligible.length || rows.some(r => selectedCreditProblem(r, freshBlock))) {
          setRevision(r => r + 1);
          throw new Error("The lessons or credit balance changed. Review the refreshed selection before applying credits.");
        }
        next = { blockId, items: eligible.map(r => ({ lesson: r.lesson, key: idempotencyKey(), state: "pending" })) };
        saveOperation(next);
      }
      for (const item of next.items) {
        if (item.state === "applied" || item.state === "failed") continue;
        // An uncertain debit must first replay unchanged; rechecking a now-paid
        // lesson would otherwise prevent recovery of its successful receipt.
        if (item.state === "pending") {
          const fresh = await staffBooking.lessonCredits(studentId);
          const freshBlock = fresh.blocks.find(b => b.lesson_block_id === next!.blockId);
          const [row] = await checkSelectedLessons(studentId, [item.lesson], true);
          if (fresh.portal_student_id !== studentId || !freshBlock || freshBlock.available_credits < 1 || !row || selectedCreditProblem(row, freshBlock)) {
            next = replaceCreditItem(next, { ...item, state: "failed", message: "The lesson or balance changed. No credit was applied to this lesson." });
            saveOperation(next);
            break;
          }
        }
        next = replaceCreditItem(next, { ...item, state: "sending" });
        saveOperation(next);
        try {
          await useExistingLessonCredit(next.blockId, {
            idempotency_key: item.key,
            amelia_appointment_id: item.lesson.appointmentId,
            portal_student_id: studentId,
            notes: "Applied from student lesson selection",
          });
          next = replaceCreditItem(next, { ...item, state: "applied", message: undefined });
          saveOperation(next);
        } catch (cause) {
          const uncertain = bookingFailureKind(cause) === "uncertain";
          next = replaceCreditItem(next, {
            ...item,
            state: uncertain ? "sending" : "failed",
            message: uncertain ? "Outcome unconfirmed. Retry this same attempt to check it." : cause instanceof Error ? cause.message : "Credit was not applied.",
          });
          saveOperation(next);
          break;
        }
      }
      // Pending items also need their original keys after a pre-check failure,
      // interrupted page, or an earlier lesson's definite rejection.
      if (next.items.every(i => i.state === "applied" || i.state === "failed")) sessionStorage.removeItem(storageKey);
    } catch (cause) {
      setProblem(cause instanceof Error ? cause.message : "The credit application could not be confirmed.");
    } finally {
      if (next) {
        setOperation(next);
        notifyStudentCreditStateChanged(studentId);
        changed.current();
        try {
          const fresh = await staffBooking.lessonCredits(studentId);
          if (fresh.portal_student_id !== studentId || !Number.isFinite(fresh.total_available_credits)) throw new Error();
          setBalance(fresh.total_available_credits);
        } catch { setProblem("The current balance could not be refreshed. Check credits before starting another application."); }
      }
      locked.current = false;
      setBusy(false);
      onBusyChange(false);
    }
  }

  const uncertain = operation?.items.some(i => i.state === "sending");
  const resumable = operation?.items.some(i => i.state === "pending" || i.state === "sending");
  const appliedCount = operation?.items.filter(i => i.state === "applied").length ?? 0;
  return <div className="stack">
    {problem && <div className="error" role="alert">{problem}</div>}
    {operation ? <>
      <ActionConfirmation variant={uncertain ? "uncertain" : operation.items.every(i => i.state === "applied") ? "success" : "attention"}
        title={uncertain ? "Credit application needs checking" : resumable ? "Credit application paused" : "Credit application results"}
        details={[{ value: `${appliedCount} lesson credit${appliedCount === 1 ? "" : "s"} applied.` }, ...(balance === null ? [] : [{ value: `${balance} credits now available.` }])]} />
      <ul className="selected-lesson-results">{operation.items.map(i => <li key={i.key}><strong>{selectedLessonLabel(i.lesson)}</strong><span>{i.state === "applied" ? "Credit applied" : i.message ?? "Not attempted"}</span></li>)}</ul>
      {resumable && <button type="button" disabled={busy} onClick={() => void run(operation)}>Retry same credit application</button>}
      {resumable && !uncertain && <>
        <p className="small muted">Discard only credits that have not been attempted. Credits already applied will remain applied.</p>
        <button className="quiet" type="button" disabled={busy} onClick={discardUnstarted}>Discard unstarted credits</button>
      </>}
    </> : <>
      {!summary || !checked ? !problem && <p className="small muted">Checking lessons and available credits…</p> : <>
        <label className="stack">Lesson block
          <select value={blockId} disabled={busy} onChange={e => setBlockId(e.target.value)}>
            <option value="">Choose a block</option>
            {blocks.map(b => <option key={b.lesson_block_id} value={b.lesson_block_id}>{b.service_name} · {b.available_credits} credits · expires {new Date(b.expires_at).toLocaleDateString("en-GB")}</option>)}
          </select>
        </label>
        {!blocks.length && <p>No credits are available for manual application.</p>}
        {diaryManualCreditUseMessages(summary.blocks).map(m => <p className="small muted" key={m}>{m}</p>)}
        {block && <>
          <p><strong>{eligible.length} of {lessons.length} selected lessons</strong> can use this block. This uses one credit per lesson; {Math.max(0, block.available_credits - eligible.length)} credits would remain in this block.</p>
          {eligible.length > block.available_credits && <div className="notice">There are not enough credits. Select fewer lessons or choose another block.</div>}
          <ul className="selected-lesson-results">{checked.map(r => <li key={r.lesson.appointmentId}><strong>{selectedLessonLabel(r.lesson)}</strong><span>{selectedCreditProblem(r, block) ?? "1 credit will be applied"}</span></li>)}</ul>
          <button type="button" disabled={busy || !eligible.length || eligible.length > block.available_credits} onClick={() => void run()}>{busy ? "Applying credits…" : `Apply ${eligible.length} credit${eligible.length === 1 ? "" : "s"}`}</button>
        </>}
      </>}
    </>}
  </div>;
}
